import { FC, useState } from 'react';
import { CustomButton } from 'components';
import CustomPopover from 'components/custom-popover/CustomPopover';
import { ButtonType } from 'components/custom-button/CustomButton';
import { DropDownChevronDownIcon } from 'assets/icons';
import { MemberPayRunStatus, PayRunMemberApprovalType } from '../constants';
import { selectCurrentTeamAccountingPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { InfoMesssages } from 'types/infoMessages';

interface ApproveButtonProps {
  amount: string;
  status: string;
  isReviewComplete: boolean;
  onApprovalAction: (type: PayRunMemberApprovalType) => void;
  disabled?: boolean;
}

const MemberApproveButton: FC<ApproveButtonProps> = ({
  amount,
  status,
  isReviewComplete,
  onApprovalAction,
  disabled = false
}) => {
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { approveAndProcessAccounting } = currentTeamAccountingPermissions ?? {};

  const [isOpen, setIsOpen] = useState(false);

  const handleTogglePopover = () => setIsOpen(!isOpen);

  if (status === MemberPayRunStatus.PENDING_APPROVAL && !isReviewComplete) {
    return (
      <CustomButton
        showDisabledReason={!approveAndProcessAccounting}
        disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
        id="approve"
        type={ButtonType.PRIMARY}
        disabled
        className="px-4"
        childrenContainerStyles="text-17px">
        Approve
      </CustomButton>
    );
  }

  if (status === MemberPayRunStatus.APPROVED || status === MemberPayRunStatus.APPROVED_FOR_PAY) {
    return (
      <CustomButton
        showDisabledReason={!approveAndProcessAccounting}
        disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
        id="mark-as-paid"
        type={ButtonType.PRIMARY}
        onClick={() => onApprovalAction(PayRunMemberApprovalType.PAID)}
        className="px-4"
        childrenContainerStyles="text-17px"
        disabled={disabled}>
        {`Mark ${amount} as paid`}
      </CustomButton>
    );
  }

  if (status === MemberPayRunStatus.PAID) {
    return (
      <CustomButton
        showDisabledReason={!approveAndProcessAccounting}
        disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
        id="paid"
        type={ButtonType.PRIMARY}
        disabled
        className="w-[100px] px-4 opacity-70"
        childrenContainerStyles="text-17px">
        Paid
      </CustomButton>
    );
  }

  return (
    <div className="flex">
      <CustomButton
        showDisabledReason={!approveAndProcessAccounting}
        disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
        id="approve"
        type={ButtonType.PRIMARY}
        onClick={() => onApprovalAction(PayRunMemberApprovalType.APPROVE)}
        className="rounded-r-none px-4"
        childrenContainerStyles="text-17px"
        disabled={disabled}>
        {`Approve ${amount} for pay`}
      </CustomButton>
      <CustomPopover
        anchorComponent={() => (
          <CustomButton
            id="approve-options"
            type={ButtonType.PRIMARY}
            onClick={handleTogglePopover}
            className={`rounded-l-none border-l border-white/20 py-1 px-3 ${
              !approveAndProcessAccounting ? 'opacity-50' : ''
            }`}
            disabled={disabled}>
            <DropDownChevronDownIcon className="path path-fill-current h-2 w-2 text-white" />
          </CustomButton>
        )}
        disabled={!approveAndProcessAccounting}
        show={isOpen}
        onTogglePopover={handleTogglePopover}
        onClosePopover={() => setIsOpen(false)}
        width="w-64"
        margin="mt-1"
        offset="-right-1"
        padding="px-0 py-1">
        <button
          className="w-full px-4 py-2 text-left text-17px hover:bg-highlightGray"
          onClick={() => {
            onApprovalAction(PayRunMemberApprovalType.APPROVE);
            setIsOpen(false);
          }}>
          {`Approve ${amount} for pay`}
        </button>
        <button
          className="w-full px-4 py-2 text-left text-17px hover:bg-highlightGray"
          onClick={() => {
            onApprovalAction(PayRunMemberApprovalType.APPROVE_AND_PAID);
            setIsOpen(false);
          }}>
          Approve and mark as paid
        </button>
      </CustomPopover>
    </div>
  );
};

export default MemberApproveButton;
